import { faMoneyCheckDollarPen } from '@fortawesome/pro-regular-svg-icons'
import { useReselector } from '@/utils/sharedHooks'
import { selectFederalEstimateByTaxQuarter } from '../../../../userTaxEstimates.selector'

// TODO: Migrate to V2
import { QuarterlyTaxEstimateDetail } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import {
  isTaxCalculationOutput,
  UserTaxCalculation,
} from '@/features/admin/user-tax-calculations/userTaxCalculation.slice'
import { FILING_STATUSES } from '@/features/taxes/taxConstants'

import {
  QuarterlyTaxesPaidToDateTooltip,
  TaxWithholdingsPaidToDateTooltip,
} from '../../EstimateBreakdownV3Tooltips'
import { centsToDollarsOrDashes, centsToSubtractedAmount } from '../../utils'

import { AccordionHeader } from '../shared/accordion-header'
import { BreakdownTooltip } from '../shared/breakdown-tooltip'
import { CalculationRow } from '../shared/calculation-row'
import { PaymentGateCallout } from '../../PaymentGateCallout'
import { useBreakdownTransparency } from '../../useBreakdownTransparency'

import { TextColors, TypographyStyles } from '@/styles'
import { Accordion, Card, Icon, Text, Badge, CardColors } from '@/ui'

const doesCalcHaveSpouse = (taxCalc: UserTaxCalculation | undefined) =>
  taxCalc?.filingStatus &&
  [
    FILING_STATUSES.married_filing_jointly,
    FILING_STATUSES.qualifying_widow,
  ].includes(taxCalc.filingStatus)

const MedicareStatement = () => (
  <Text color={TextColors.MUTED}>
    <i>
      Currently, we don&apos;t use your W-2 medicare and social security
      withholdings to calculate the amount of taxes you&apos;ve paid to date.
      We&apos;re working on including these withholdings in future quarters.
    </i>
  </Text>
)

interface TaxesPaidToDateSectionProps {
  taxCalc: UserTaxCalculation | undefined
  estimateDetails: QuarterlyTaxEstimateDetail | undefined
  isSample?: boolean
}

export const TaxesPaidToDateSection = ({
  taxCalc,
  estimateDetails,
  isSample,
}: TaxesPaidToDateSectionProps) => {
  const quarter = estimateDetails?.taxQuarter
  const year = estimateDetails?.taxYear

  const displaySampleVal = Boolean(taxCalc && isSample)

  const q1Estimate = useReselector(
    selectFederalEstimateByTaxQuarter,
    `${year}-1`
  )
  const q2Estimate = useReselector(
    selectFederalEstimateByTaxQuarter,
    `${year}-2`
  )
  const q3Estimate = useReselector(
    selectFederalEstimateByTaxQuarter,
    `${year}-3`
  )
  const quarterNum = Number(quarter)
  const taxCalculationOutput = taxCalc?.taxCalculationOutput
  const hasSpouse = doesCalcHaveSpouse(taxCalc)
  const sampleEstimateAmount = 83173
  const transparencyEnabled = useBreakdownTransparency()

  // A quarter with nothing logged has genuinely had $0 paid toward it; dashes
  // read as "we don't know", which is what this section is meant to stop doing.
  const formatQuarterPayment = (amountInCents: number | null | undefined) =>
    transparencyEnabled
      ? centsToSubtractedAmount(amountInCents ?? 0)
      : centsToDollarsOrDashes(amountInCents, true)

  const paymentsLoggedToDateInCents = [
    q1Estimate,
    quarterNum > 2 ? q2Estimate : undefined,
    quarterNum > 3 ? q3Estimate : undefined,
  ].reduce((total, estimate) => total + (estimate?.amountPaidInCents ?? 0), 0)

  if (
    !isTaxCalculationOutput(taxCalculationOutput) &&
    taxCalculationOutput !== undefined
  ) {
    return null
  }

  return (
    <>
      <div className="flex items-center mb-4">
        <Text variant={TypographyStyles.H2} className="flex items-center">
          <Icon icon={faMoneyCheckDollarPen} className="mr-2" />
          Taxes Paid
        </Text>
        {isSample && (
          <Badge color="orange" className="ml-4">
            Sample Data
          </Badge>
        )}
      </div>

      <Text className="w-full lg:w-8/12 mb-6" color={TextColors.MUTED}>
        We take this quarter&apos;s tax bill and if applicable, we factor in any
        quarterly tax payments made this year and any tax withholdings that have
        been paid to date.
      </Text>
      <CalculationRow
        left={<Text variant={TypographyStyles.H3}>Q{quarter} Taxes Due</Text>}
        right={
          <Text variant={TypographyStyles.H3}>
            {centsToDollarsOrDashes(
              taxCalculationOutput?.data.YTDGrossFederalTaxLiabilityInCents
            )}
          </Text>
        }
      />
      {quarterNum > 1 && (
        <div className="mt-4">
          <PaymentGateCallout
            allQuarterPaymentsLogged={
              taxCalculationOutput?.data.allFederalQuarterPaymentsLogged
            }
            quarter={quarter}
          />
          <Accordion variant="text" type="single" collapsible>
            <Accordion.Item value="paidToDate">
              <Accordion.Trigger className="w-full">
                <AccordionHeader
                  left={
                    <BreakdownTooltip
                      trigger="Quarterly Taxes Paid to Date"
                      content={<QuarterlyTaxesPaidToDateTooltip />}
                    />
                  }
                  right={
                    <Text variant={TypographyStyles.H3}>
                      {transparencyEnabled
                        ? centsToSubtractedAmount(paymentsLoggedToDateInCents)
                        : centsToDollarsOrDashes(
                            taxCalculationOutput?.data
                              .YTDFederalTaxPaymentsMadeInCents,
                            true
                          )}
                    </Text>
                  }
                />
              </Accordion.Trigger>
              <Accordion.Content>
                <Card color={CardColors.LEVEL_1} className="p-4">
                  <div className="space-y-4 p-4">
                    <CalculationRow
                      left={
                        <Text
                          variant={TypographyStyles.EYEBROW}
                          color={TextColors.MUTED}
                        >
                          payment type
                        </Text>
                      }
                      right={
                        <Text
                          variant={TypographyStyles.EYEBROW}
                          color={TextColors.MUTED}
                          className="text-right"
                        >
                          amount paid
                        </Text>
                      }
                      inCard
                    />
                    <CalculationRow
                      left={<Text>Q1 tax payment</Text>}
                      right={
                        <Text variant={TypographyStyles.H3}>
                          {formatQuarterPayment(
                            displaySampleVal
                              ? sampleEstimateAmount
                              : q1Estimate?.amountPaidInCents
                          )}
                        </Text>
                      }
                      inCard
                    />
                    {quarterNum > 2 && (
                      <CalculationRow
                        left={<Text>Q2 tax payment</Text>}
                        right={
                          <Text variant={TypographyStyles.H3}>
                            {formatQuarterPayment(
                              displaySampleVal
                                ? sampleEstimateAmount
                                : q2Estimate?.amountPaidInCents
                            )}
                          </Text>
                        }
                        inCard
                      />
                    )}
                    {quarterNum > 3 && (
                      <CalculationRow
                        left={<Text>Q3 tax payment</Text>}
                        right={
                          <Text variant={TypographyStyles.H3}>
                            {formatQuarterPayment(
                              displaySampleVal
                                ? sampleEstimateAmount
                                : q3Estimate?.amountPaidInCents
                            )}
                          </Text>
                        }
                        inCard
                      />
                    )}
                  </div>
                </Card>
              </Accordion.Content>
            </Accordion.Item>
          </Accordion>
        </div>
      )}

      <div className="mt-4">
        <Accordion variant="text" type="single" collapsible>
          <Accordion.Item value="withholdings">
            <Accordion.Trigger className="w-full">
              <AccordionHeader
                left={
                  <BreakdownTooltip
                    trigger="Projected Tax Withholdings"
                    content={<TaxWithholdingsPaidToDateTooltip />}
                  />
                }
                right={
                  <Text variant={TypographyStyles.H3}>
                    {centsToDollarsOrDashes(
                      taxCalculationOutput?.data
                        .annualTotalFederalWithholdingInCents,
                      true
                    )}
                  </Text>
                }
              />
            </Accordion.Trigger>
            <Accordion.Content>
              <Card color={CardColors.LEVEL_1} className="p-4">
                <div className="space-y-4 p-4">
                  <CalculationRow
                    left={
                      <Text
                        variant={TypographyStyles.EYEBROW}
                        color={TextColors.MUTED}
                      >
                        payment type
                      </Text>
                    }
                    center={
                      <Text
                        variant={TypographyStyles.EYEBROW}
                        color={TextColors.MUTED}
                        className="text-right"
                      >
                        Your Entries
                        <br />
                        (Year-to-Date)
                      </Text>
                    }
                    right={
                      <Text
                        variant={TypographyStyles.EYEBROW}
                        color={TextColors.MUTED}
                        className="text-right"
                      >
                        Projected
                        <br />
                        Withholding
                      </Text>
                    }
                    inCard
                  />

                  <CalculationRow
                    left={
                      <>
                        <Text>Tax withholdings</Text>
                        <div className="hidden md:block">
                          <MedicareStatement />
                        </div>
                      </>
                    }
                    center={
                      <Text variant={TypographyStyles.H3}>
                        {centsToDollarsOrDashes(
                          taxCalc?.federalWithholdingInCents,
                          true
                        )}
                      </Text>
                    }
                    right={
                      <Text variant={TypographyStyles.H3}>
                        {centsToDollarsOrDashes(
                          taxCalc?.taxCalculationOutput?.data
                            ?.projectedFederalWithholdingInCents,
                          true
                        )}
                      </Text>
                    }
                    inCard
                  />

                  {/* show on mobile only */}
                  <div className="block md:hidden">
                    <MedicareStatement />
                  </div>

                  {hasSpouse && (
                    <CalculationRow
                      left={<Text>Spouse&apos;s tax withholdings</Text>}
                      center={
                        <Text variant={TypographyStyles.H3}>
                          {centsToDollarsOrDashes(
                            taxCalc?.spouseFederalWithholdingInCents,
                            true
                          )}
                        </Text>
                      }
                      right={
                        <Text variant={TypographyStyles.H3}>
                          {centsToDollarsOrDashes(
                            taxCalc?.taxCalculationOutput?.data
                              ?.projectedSpouseFederalWithholdingInCents,
                            true
                          )}
                        </Text>
                      }
                      inCard
                    />
                  )}
                </div>
              </Card>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion>
      </div>
    </>
  )
}
